// =============================================
// ProgressLogModal — full progress log + llm stats per stage
// =============================================
// Otwierany po kliku "Pelen log" w wierszu QueueDashboard.
// Realtime: zywy view item.config.progress_log z useGenerationQueue +
// dofetchuje llm_call_events per nodeId dla tokens/cost/model breakdown.

import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Terminal, Coins, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface ProgressEntry {
  t: string;
  stage: string;
  label: string;
  meta?: Record<string, unknown>;
}

interface LlmStatRow {
  step: string | null;
  model: string;
  calls: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeTitle: string;
  nodeId?: string;
  entries: ProgressEntry[];
}

const STAGE_COLORS: Record<string, string> = {
  serp_research: "text-cyan-600 dark:text-cyan-400",
  kg_assembly: "text-purple-600 dark:text-purple-400",
  fan_out: "text-blue-600 dark:text-blue-400",
  outline: "text-violet-600 dark:text-violet-400",
  outline_ready: "text-violet-700 dark:text-violet-300 font-medium",
  draft: "text-amber-600 dark:text-amber-400",
  enrichment: "text-amber-600 dark:text-amber-400",
  optimization: "text-orange-600 dark:text-orange-400",
  humanization: "text-pink-600 dark:text-pink-400",
  ready: "text-green-700 dark:text-green-400 font-medium",
  failed: "text-red-600 dark:text-red-400 font-medium",
};

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
}

function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function formatCost(n: number): string {
  if (n === 0) return "$0.000";
  if (n < 0.001) return "<$0.001";
  if (n < 1) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

function shortModel(m: string): string {
  return m.replace(/^gpt-/, "").replace(/^claude-/, "");
}

export function ProgressLogModal({ open, onOpenChange, nodeTitle, nodeId, entries }: Props) {
  const endRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState<LlmStatRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [open, entries.length]);

  // Dofetch llm_call_events per nodeId gdy modal open
  useEffect(() => {
    if (!open || !nodeId) { setStats([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await (supabase as any)
        .from("llm_call_events")
        .select("step, model, tokens_input, tokens_output, cost_usd")
        .eq("node_id", nodeId);
      if (cancelled) return;
      const rows = (data || []) as Array<{ step: string | null; model: string; tokens_input: number; tokens_output: number; cost_usd: number | string }>;
      const map = new Map<string, LlmStatRow>();
      for (const r of rows) {
        const key = `${r.step ?? "?"}|${r.model}`;
        const agg = map.get(key) ?? { step: r.step, model: r.model, calls: 0, tokensIn: 0, tokensOut: 0, costUsd: 0 };
        agg.calls += 1;
        agg.tokensIn += Number(r.tokens_input || 0);
        agg.tokensOut += Number(r.tokens_output || 0);
        agg.costUsd += Number(r.cost_usd || 0);
        map.set(key, agg);
      }
      setStats([...map.values()].sort((a, b) => b.costUsd - a.costUsd));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, nodeId]);

  const totalCalls = stats.reduce((s, r) => s + r.calls, 0);
  const totalTokens = stats.reduce((s, r) => s + r.tokensIn + r.tokensOut, 0);
  const totalCost = stats.reduce((s, r) => s + r.costUsd, 0);

  const handleDownload = () => {
    const txtLines = [
      `# Progress log: ${nodeTitle}`,
      ``,
      `## Summary`,
      `calls: ${totalCalls}, tokens: ${totalTokens}, cost: ${formatCost(totalCost)}`,
      ``,
      `## Per step / model`,
      ...stats.map((s) =>
        `${s.step ?? "(unknown)"} · ${s.model} · ${s.calls} calls · ${s.tokensIn}+${s.tokensOut} tokens · ${formatCost(s.costUsd)}`,
      ),
      ``,
      `## Timeline`,
      ...entries.map((e) => `[${formatTime(e.t)}] [${e.stage}] ${e.label}`),
    ];
    const blob = new Blob([txtLines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `progress_${nodeTitle.replace(/[^a-z0-9]/gi, "_").slice(0, 40)}_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            Progress log: <span className="font-mono text-sm text-muted-foreground truncate">{nodeTitle}</span>
            <Button size="sm" variant="ghost" onClick={handleDownload} className="ml-auto h-7 text-xs">
              <Download className="h-3 w-3 mr-1" /> Pobierz TXT
            </Button>
          </DialogTitle>
        </DialogHeader>

        {/* Summary scorecards */}
        {nodeId && stats.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            <div className="border rounded-md p-2.5 bg-muted/30">
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wide">
                <Zap className="h-3 w-3" /> Calls
              </div>
              <p className="text-lg font-semibold tabular-nums">{totalCalls}</p>
            </div>
            <div className="border rounded-md p-2.5 bg-muted/30">
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wide">
                <Terminal className="h-3 w-3" /> Tokens
              </div>
              <p className="text-lg font-semibold tabular-nums">{formatTokens(totalTokens)}</p>
            </div>
            <div className="border rounded-md p-2.5 bg-green-500/5 border-green-500/30">
              <div className="flex items-center gap-1.5 text-[10px] text-green-700 dark:text-green-400 uppercase tracking-wide">
                <Coins className="h-3 w-3" /> Koszt
              </div>
              <p className="text-lg font-semibold tabular-nums text-green-700 dark:text-green-400">{formatCost(totalCost)}</p>
            </div>
          </div>
        )}

        {/* Per-step / model breakdown */}
        {nodeId && stats.length > 0 && (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-1.5 px-2 font-medium text-muted-foreground uppercase text-[10px] tracking-wide">Step</th>
                  <th className="text-left py-1.5 px-2 font-medium text-muted-foreground uppercase text-[10px] tracking-wide">Model</th>
                  <th className="text-right py-1.5 px-2 font-medium text-muted-foreground uppercase text-[10px] tracking-wide">Calls</th>
                  <th className="text-right py-1.5 px-2 font-medium text-muted-foreground uppercase text-[10px] tracking-wide">In</th>
                  <th className="text-right py-1.5 px-2 font-medium text-muted-foreground uppercase text-[10px] tracking-wide">Out</th>
                  <th className="text-right py-1.5 px-2 font-medium text-muted-foreground uppercase text-[10px] tracking-wide">Koszt</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((r, i) => (
                  <tr key={i} className="border-t border-border hover:bg-muted/30">
                    <td className={`py-1 px-2 ${STAGE_COLORS[r.step ?? ""] ?? "text-foreground"}`}>{r.step ?? <span className="italic text-muted-foreground">(brak)</span>}</td>
                    <td className="py-1 px-2 font-mono text-[10px] text-muted-foreground">{shortModel(r.model)}</td>
                    <td className="py-1 px-2 text-right tabular-nums">{r.calls}</td>
                    <td className="py-1 px-2 text-right tabular-nums text-muted-foreground">{formatTokens(r.tokensIn)}</td>
                    <td className="py-1 px-2 text-right tabular-nums text-muted-foreground">{formatTokens(r.tokensOut)}</td>
                    <td className="py-1 px-2 text-right tabular-nums font-medium text-green-700 dark:text-green-400">{formatCost(r.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {nodeId && !loading && stats.length === 0 && (
          <div className="border rounded-md p-3 text-[11px] text-muted-foreground italic bg-muted/20">
            Brak danych cost tracking dla tego node'a w <code>llm_call_events</code>. Pipeline zostal odpalony przed deployem hooka trackLlmCall (commit 8ffd382) albo tabela pusta.
          </div>
        )}

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto bg-background border rounded-md p-3 font-mono text-xs space-y-0.5 min-h-[200px]">
          {entries.length === 0 ? (
            <p className="text-muted-foreground italic">Brak wpisow w logu (worker jeszcze nie wystartowal lub progress_log nieobsugiwany przez stary worker).</p>
          ) : (
            entries.map((e, i) => (
              <div key={i} className="flex gap-2">
                <span className="text-muted-foreground/60 shrink-0">{formatTime(e.t)}</span>
                <span className={`shrink-0 ${STAGE_COLORS[e.stage] || "text-muted-foreground"}`}>[{e.stage}]</span>
                <span className="text-foreground">{e.label}</span>
              </div>
            ))
          )}
          <div ref={endRef} />
        </div>

        <div className="text-[10px] text-muted-foreground flex items-center justify-between">
          <span>{entries.length} wpisow · max 200 · debounce 500ms</span>
          {stats.length > 0 && <span>Cost data: {totalCalls} calls w llm_call_events</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
